/**
 * Header Component
 *
 * Темно-синий хедер портала
 * - Кликабельный логотип (ведёт на главную)
 * - Иконки утилит (уведомления, настройки, помощь)
 * - Профиль пользователя
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { AppShell, Group, ActionIcon, Tooltip, Box } from '@mantine/core';
import {
  IconBell, IconHelp, IconSettings, IconUser, IconMenu2
} from '@tabler/icons-react';
import tokens from '../theme/designTokens';
import { useSession } from '../context/SessionContext';
import ProfileCard from './ProfileCard';
import ChangePasswordModal from './ChangePasswordModal';

const Header = ({ mobileOpened, onMobileToggle }) => {
  const navigate = useNavigate();
  const { user, logout, uploadAvatar } = useSession();
  const [profileOpened, setProfileOpened] = useState(false);
  const [changePasswordOpened, setChangePasswordOpened] = useState(false);
  const profileRef = useRef(null);
  const buttonRef = useRef(null);

  // Закрыть при клике вне карточки
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileOpened &&
        profileRef.current &&
        !profileRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setProfileOpened(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpened]);

  const handleLogout = async () => {
    setProfileOpened(false);
    await logout();
    navigate('/login');
  };

  const handleChangePassword = () => {
    setProfileOpened(false);
    setChangePasswordOpened(true);
  };

  const handleUploadAvatar = async (file) => {
    try {
      await uploadAvatar(file);
    } catch (error) {
      console.error('Failed to upload avatar:', error);
    }
  };

  return (
    <>
    <AppShell.Header
      style={{
        backgroundColor: '#032D60',
        borderBottom: 'none',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
      }}
    >
      <Group h="100%" px="md" justify="space-between" wrap="nowrap">
        {/* Left: Burger + Logo */}
        <Group gap="xs" wrap="nowrap">
          {/* Hamburger menu - visible only on mobile */}
          <ActionIcon
            variant="transparent"
            size="lg"
            onClick={onMobileToggle}
            style={{ color: 'white' }}
            hiddenFrom="sm"
            aria-label="Toggle navigation"
          >
            <IconMenu2 size={24} />
          </ActionIcon>

          <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/images/logos/logo-white.png"
              alt="It Is Interesting"
              style={{ height: 44, cursor: 'pointer' }}
            />
          </Link>
        </Group>

        {/* Right: Icons + User - Адаптивные */}
        <Group gap="xs" wrap="nowrap">
          {/* Скрыты на мобильных для экономии места */}
          <Box
            sx={{
              display: 'none',
              '@media (min-width: 768px)': {
                display: 'block',
              },
            }}
          >
            <Tooltip label="Помощь">
              <ActionIcon variant="transparent" size="lg" style={{ color: 'white' }}>
                <IconHelp size={20} />
              </ActionIcon>
            </Tooltip>
          </Box>
          <Box
            sx={{
              display: 'none',
              '@media (min-width: 768px)': {
                display: 'block',
              },
            }}
          >
            <Tooltip label="Настройки">
              <ActionIcon variant="transparent" size="lg" style={{ color: 'white' }}>
                <IconSettings size={20} />
              </ActionIcon>
            </Tooltip>
          </Box>
          <Tooltip label="Уведомления">
            <ActionIcon variant="transparent" size="lg" style={{ color: 'white' }}>
              <IconBell size={20} />
            </ActionIcon>
          </Tooltip>

          {/* Profile Button & Dropdown */}
          <Box style={{ position: 'relative' }}>
            <Tooltip label="Профиль" disabled={profileOpened}>
              <ActionIcon
                ref={buttonRef}
                variant="filled"
                size="lg"
                onClick={() => setProfileOpened((o) => !o)}
                style={{
                  backgroundColor: tokens.colors.brand.primary,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  overflow: 'hidden',
                }}
              >
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt="Avatar"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <IconUser size={18} />
                )}
              </ActionIcon>
            </Tooltip>

          </Box>
        </Group>
      </Group>

      {/* Profile Card Portal - рендерится вне Header для избежания обрезки */}
      {profileOpened && createPortal(
        <Box
          ref={profileRef}
          style={{
            position: 'fixed',
            top: 60,
            right: 16,
            zIndex: 9999,
            width: 320,
          }}
        >
          <ProfileCard
            user={user}
            onLogout={handleLogout}
            onChangePassword={handleChangePassword}
            onUploadAvatar={handleUploadAvatar}
          />
        </Box>,
        document.body
      )}
    </AppShell.Header>

      {/* Модальное окно смены пароля */}
      <ChangePasswordModal
        opened={changePasswordOpened}
        onClose={() => setChangePasswordOpened(false)}
      />
    </>
  );
};

export default Header;
